import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import useBreakpoint, { Breakpoints } from './useBreakpoint';

export enum Pdp2026ScrollState {
  Idle = 'idle',
  RightPanelScrollToCta = 'rightPanelScrollToCta',
  LeftGalleryScroll = 'leftGalleryScroll',
  RightSecondaryScroll = 'rightSecondaryScroll',
  FullPageScroll = 'fullPageScroll',
}

interface UsePdp2026ScrollInteractionOptions {
  enabled: boolean;
  containerRef: RefObject<HTMLElement | null>;
  leftColumnRef: RefObject<HTMLElement | null>;
  rightColumnRef: RefObject<HTMLElement | null>;
  galleryItemCount?: number;
}

const BOTTOM_OFFSET_PX = 16;
const SCROLL_START_PX = 4;
const RIGHT_COLUMN_STICKY_TOP_MAX_PX = 130;
const RIGHT_COLUMN_STICKY_TOP_CSS_VAR = '--pdp-2026-right-column-top';
const LEFT_COLUMN_SCROLL_STICKY_TOP_PX = 0;
const LEFT_COLUMN_SCROLL_REQUIRED_OVERFLOW_PX = 0;
const LEFT_GALLERY_RELEASE_HYSTERESIS_PX = 120;
const LEFT_GALLERY_RESET_SCROLL_BACKTRACK_PX = 120;
const CTA_BOTTOM_ALIGNMENT_TOLERANCE_PX = 24;
const SCROLL_STATE_ORDER: Record<Pdp2026ScrollState, number> = {
  [Pdp2026ScrollState.Idle]: 0,
  [Pdp2026ScrollState.RightPanelScrollToCta]: 1,
  [Pdp2026ScrollState.LeftGalleryScroll]: 2,
  [Pdp2026ScrollState.RightSecondaryScroll]: 3,
  [Pdp2026ScrollState.FullPageScroll]: 4,
};

const usePdp2026ScrollInteraction = ({
  enabled,
  containerRef,
  leftColumnRef,
  rightColumnRef,
  galleryItemCount,
}: UsePdp2026ScrollInteractionOptions) => {
  const getRenderedGalleryItemCount = (leftColumnEl: HTMLElement) => {
    const galleryRootWithCount = leftColumnEl.querySelector<HTMLElement>(
      '[data-pdp-gallery-item-count]'
    );
    const galleryCountFromAttribute = galleryRootWithCount?.dataset
      ?.pdpGalleryItemCount
      ? Number(galleryRootWithCount.dataset.pdpGalleryItemCount)
      : null;

    if (
      galleryCountFromAttribute !== null &&
      Number.isFinite(galleryCountFromAttribute)
    ) {
      return galleryCountFromAttribute;
    }

    return leftColumnEl.querySelectorAll('[data-testid^="image-gallery-main-"]')
      .length;
  };

  const getHeaderStickyTopMax = () => {
    const headerEl = document.querySelector<HTMLElement>('header');
    if (!headerEl) {
      return RIGHT_COLUMN_STICKY_TOP_MAX_PX;
    }

    const headerRect = headerEl.getBoundingClientRect();
    return Math.max(
      0,
      Math.min(RIGHT_COLUMN_STICKY_TOP_MAX_PX, Math.round(headerRect.bottom))
    );
  };

  const isBelowDesktop = useBreakpoint(Breakpoints.tabletXl);
  const [scrollState, setScrollState] = useState<Pdp2026ScrollState>(
    Pdp2026ScrollState.Idle
  );
  const rightColumnStickyTopRef = useRef<number | null>(null);
  const scrollStateRef = useRef<Pdp2026ScrollState>(Pdp2026ScrollState.Idle);
  const leftGalleryCompletedRef = useRef(false);
  const leftColumnWasFullyAboveRef = useRef(false);
  const hasScrolledPastLeftColumnRef = useRef(false);
  const maxScrollYRef = useRef(0);
  const lastScrollDirectionRef = useRef<'down' | 'up' | 'none'>('none');

  const isActive = enabled && !isBelowDesktop;

  useEffect(() => {
    if (!isActive) {
      scrollStateRef.current = Pdp2026ScrollState.Idle;
      setScrollState(Pdp2026ScrollState.Idle);
      rightColumnStickyTopRef.current = null;
      rightColumnRef.current?.style.removeProperty(
        RIGHT_COLUMN_STICKY_TOP_CSS_VAR
      );
      leftGalleryCompletedRef.current = false;
      leftColumnWasFullyAboveRef.current = false;
      hasScrolledPastLeftColumnRef.current = false;
      maxScrollYRef.current = 0;
      lastScrollDirectionRef.current = 'none';
      return;
    }

    let frameId = 0;
    let lastScrollY = window.scrollY;

    const measureAndUpdate = () => {
      const containerEl = containerRef.current;
      const leftColumnEl = leftColumnRef.current;
      const rightColumnEl = rightColumnRef.current;

      const commitState = (
        nextState: Pdp2026ScrollState,
        nextStickyTop: number | null
      ) => {
        const stateChanged = scrollStateRef.current !== nextState;
        const stickyTopChanged =
          rightColumnStickyTopRef.current !== nextStickyTop;

        scrollStateRef.current = nextState;
        rightColumnStickyTopRef.current = nextStickyTop;

        // The sticky offset changes almost every frame during the sticky
        // sequence, so write it straight to the DOM as a CSS variable instead
        // of routing it through React state (which would re-render the whole
        // PDP tree each frame). scrollState still goes through setState — it
        // only changes on discrete transitions and drives class names — and is
        // guarded so unchanged frames don't re-render at all.
        // Only ever write a concrete value here; never remove. On exit, React
        // drops the .pdp2026RightColumnFixed class one commit later, so a stale
        // var is inert (the class gates consumption). Removing it synchronously
        // would flash the SCSS 130px fallback for that one lingering-class frame.
        // Full teardown still clears it via the !isActive branch above.
        if (stickyTopChanged && rightColumnEl && nextStickyTop !== null) {
          rightColumnEl.style.setProperty(
            RIGHT_COLUMN_STICKY_TOP_CSS_VAR,
            `${nextStickyTop}px`
          );
        }
        if (stateChanged) {
          setScrollState(nextState);
        }
      };

      if (!containerEl || !leftColumnEl || !rightColumnEl) {
        commitState(Pdp2026ScrollState.Idle, null);
        return;
      }

      const renderedGalleryItemCount =
        getRenderedGalleryItemCount(leftColumnEl);
      const effectiveGalleryItemCount =
        renderedGalleryItemCount > 0
          ? renderedGalleryItemCount
          : galleryItemCount;
      const hasSmallGallery =
        effectiveGalleryItemCount !== undefined &&
        effectiveGalleryItemCount <= 3;

      if (hasSmallGallery) {
        leftGalleryCompletedRef.current = false;
        leftColumnWasFullyAboveRef.current = false;
        hasScrolledPastLeftColumnRef.current = false;
        commitState(Pdp2026ScrollState.FullPageScroll, null);

        return;
      }

      const ctaAnchor = rightColumnEl.querySelector<HTMLElement>(
        '[data-pdp-scroll-cta-anchor]'
      );
      const secondaryInfo = rightColumnEl.querySelector<HTMLElement>(
        '[data-pdp-scroll-secondary-info]'
      );

      if (!ctaAnchor || !secondaryInfo) {
        commitState(Pdp2026ScrollState.FullPageScroll, null);

        return;
      }

      const containerRect = containerEl.getBoundingClientRect();
      const leftColumnRect = leftColumnEl.getBoundingClientRect();
      const rightColumnRect = rightColumnEl.getBoundingClientRect();
      const ctaAnchorRect = ctaAnchor?.getBoundingClientRect();
      const viewportBottom = window.innerHeight;
      const leftColumnNeedsSequentialScroll =
        leftColumnRect.height >
        viewportBottom -
          BOTTOM_OFFSET_PX +
          LEFT_COLUMN_SCROLL_REQUIRED_OVERFLOW_PX;
      const stickyTopMax = getHeaderStickyTopMax();
      const containerHasStarted = containerRect.top <= -SCROLL_START_PX;
      const currentScrollY = window.scrollY;
      maxScrollYRef.current = Math.max(maxScrollYRef.current, currentScrollY);
      const scrollDirection =
        currentScrollY > lastScrollY
          ? 'down'
          : currentScrollY < lastScrollY
          ? 'up'
          : 'none';
      const effectiveScrollDirection =
        scrollDirection === 'none'
          ? lastScrollDirectionRef.current
          : scrollDirection;
      const scrollBacktrackPx = Math.max(
        0,
        maxScrollYRef.current - currentScrollY
      );
      if (scrollDirection !== 'none') {
        lastScrollDirectionRef.current = scrollDirection;
      }
      lastScrollY = currentScrollY;

      if (!containerHasStarted) {
        leftGalleryCompletedRef.current = false;
        leftColumnWasFullyAboveRef.current = false;
        hasScrolledPastLeftColumnRef.current = false;
        maxScrollYRef.current = currentScrollY;
        commitState(Pdp2026ScrollState.Idle, null);

        return;
      }

      if (!leftColumnNeedsSequentialScroll) {
        leftGalleryCompletedRef.current = false;
        leftColumnWasFullyAboveRef.current = false;
        hasScrolledPastLeftColumnRef.current = false;
        commitState(Pdp2026ScrollState.FullPageScroll, null);

        return;
      }

      if (leftColumnRect.bottom <= 0) {
        hasScrolledPastLeftColumnRef.current = true;
        if (!leftColumnWasFullyAboveRef.current) {
          // First frame where the left column is fully above the viewport:
          // clear sticky top refs so the right column does not carry a
          // bottom-anchored sticky value into the return-from-below phase.
          rightColumnStickyTopRef.current = null;
        }
        leftColumnWasFullyAboveRef.current = true;
      } else if (
        effectiveScrollDirection === 'down' &&
        leftColumnRect.bottom > viewportBottom
      ) {
        // Keep "returning from below" latched while moving upward so CTA
        // bottom-zone logic can trigger sticky at the intended point.
        // Reset only after direction switches back to downward.
        leftColumnWasFullyAboveRef.current = false;
      }

      if (
        effectiveScrollDirection === 'up' &&
        leftColumnRect.top >= LEFT_COLUMN_SCROLL_STICKY_TOP_PX
      ) {
        hasScrolledPastLeftColumnRef.current = false;
      }

      const isReturningFromBelow =
        effectiveScrollDirection === 'up' &&
        hasScrolledPastLeftColumnRef.current &&
        leftColumnRect.top < LEFT_COLUMN_SCROLL_STICKY_TOP_PX;
      const ctaBottomTarget = viewportBottom - BOTTOM_OFFSET_PX;
      const ctaHasReachedViewportBottom = Boolean(
        ctaAnchorRect &&
          ctaAnchorRect.bottom <= ctaBottomTarget &&
          ctaAnchorRect.top < ctaBottomTarget
      );
      const ctaIsInReturnBottomZone = Boolean(
        ctaAnchorRect &&
          ctaAnchorRect.bottom >=
            ctaBottomTarget - CTA_BOTTOM_ALIGNMENT_TOLERANCE_PX &&
          ctaAnchorRect.top < ctaBottomTarget
      );
      const ctaAlignedToViewportBottom = isReturningFromBelow
        ? ctaIsInReturnBottomZone
        : ctaHasReachedViewportBottom;

      const previousState = scrollStateRef.current;
      const hasReachedCtaSequence =
        ctaAlignedToViewportBottom ||
        (!isReturningFromBelow &&
          (previousState === Pdp2026ScrollState.LeftGalleryScroll ||
            previousState === Pdp2026ScrollState.RightSecondaryScroll));
      const leftGalleryReachedEndRaw =
        leftColumnRect.bottom <= viewportBottom - BOTTOM_OFFSET_PX;
      const leftGalleryResetThreshold =
        viewportBottom - BOTTOM_OFFSET_PX + LEFT_GALLERY_RELEASE_HYSTERESIS_PX;

      if (leftGalleryReachedEndRaw) {
        leftGalleryCompletedRef.current = true;
      } else if (
        effectiveScrollDirection === 'up' &&
        leftColumnRect.bottom >= leftGalleryResetThreshold &&
        scrollBacktrackPx >= LEFT_GALLERY_RESET_SCROLL_BACKTRACK_PX
      ) {
        leftGalleryCompletedRef.current = false;
      }

      const leftGalleryReachedEnd =
        leftGalleryReachedEndRaw || leftGalleryCompletedRef.current;
      let nextState: Pdp2026ScrollState =
        Pdp2026ScrollState.RightPanelScrollToCta;

      if (hasReachedCtaSequence) {
        if (!leftGalleryReachedEnd) {
          nextState = Pdp2026ScrollState.LeftGalleryScroll;
        } else {
          nextState = Pdp2026ScrollState.RightSecondaryScroll;
        }
      }

      const previousOrder = SCROLL_STATE_ORDER[previousState];
      const nextOrder = SCROLL_STATE_ORDER[nextState];
      let resolvedState: Pdp2026ScrollState = nextState;

      if (effectiveScrollDirection === 'down' && nextOrder < previousOrder) {
        resolvedState = previousState;
      }

      if (effectiveScrollDirection === 'up' && nextOrder > previousOrder) {
        resolvedState = previousState;
      }

      const shouldLockRightSecondaryOnReturn =
        isReturningFromBelow &&
        leftColumnRect.top < LEFT_COLUMN_SCROLL_STICKY_TOP_PX;
      if (shouldLockRightSecondaryOnReturn) {
        resolvedState = Pdp2026ScrollState.RightSecondaryScroll;
        leftGalleryCompletedRef.current = true;
      }

      const wasSticky =
        previousState === Pdp2026ScrollState.LeftGalleryScroll ||
        previousState === Pdp2026ScrollState.RightSecondaryScroll;
      const isNowSticky =
        resolvedState === Pdp2026ScrollState.LeftGalleryScroll ||
        resolvedState === Pdp2026ScrollState.RightSecondaryScroll;
      const isLeftGalleryStickyFlow =
        resolvedState === Pdp2026ScrollState.LeftGalleryScroll;
      const isRightSecondaryStickyFlow =
        resolvedState === Pdp2026ScrollState.RightSecondaryScroll;
      const shouldAnchorCtaToBottomInStickyFlow =
        isLeftGalleryStickyFlow || isRightSecondaryStickyFlow;

      let nextStickyTop = rightColumnStickyTopRef.current;

      if (!isNowSticky) {
        nextStickyTop = null;
      } else if (!wasSticky) {
        if (resolvedState === Pdp2026ScrollState.RightSecondaryScroll) {
          // Entering right-secondary should pin CTA near viewport bottom
          // immediately on both scroll directions.
          const ctaBottomOffsetInColumn =
            ctaAnchorRect.bottom - rightColumnRect.top;
          nextStickyTop = ctaBottomTarget - ctaBottomOffsetInColumn;
        } else if (resolvedState === Pdp2026ScrollState.LeftGalleryScroll) {
          // First left-gallery sticky frame:
          // - preserve no-snap behavior (don't move downward on entry), and
          // - prevent one-frame off-screen CTA when it is already below target.
          const ctaBottomOffsetInColumn =
            ctaAnchorRect.bottom - rightColumnRect.top;
          const desiredStickyTop = ctaBottomTarget - ctaBottomOffsetInColumn;
          const currentTop = Math.min(stickyTopMax, rightColumnRect.top);
          nextStickyTop =
            desiredStickyTop < currentTop ? desiredStickyTop : currentTop;
        } else {
          nextStickyTop = Math.min(stickyTopMax, rightColumnRect.top);
        }
      } else if (shouldAnchorCtaToBottomInStickyFlow) {
        const ctaBottomOffsetInColumn =
          ctaAnchorRect.bottom - rightColumnRect.top;
        const desiredStickyTop = ctaBottomTarget - ctaBottomOffsetInColumn;

        if (isLeftGalleryStickyFlow) {
          // Left-gallery down-scroll must never move sticky top downward
          // frame-to-frame; downward movement is perceived as snap.
          nextStickyTop =
            nextStickyTop === null
              ? desiredStickyTop
              : Math.min(nextStickyTop, desiredStickyTop);
        } else {
          nextStickyTop = desiredStickyTop;
        }
      }

      commitState(resolvedState, nextStickyTop);
    };

    const requestMeasure = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measureAndUpdate);
    };

    requestMeasure();
    window.addEventListener('scroll', requestMeasure, { passive: true });
    window.addEventListener('resize', requestMeasure);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', requestMeasure);
      window.removeEventListener('resize', requestMeasure);
    };
  }, [isActive, containerRef, leftColumnRef, rightColumnRef, galleryItemCount]);

  const flags = useMemo(
    () => ({
      isActive,
      isLeftColumnFixed: isActive && scrollState !== Pdp2026ScrollState.Idle,
      leftColumnStickyTop: isActive
        ? scrollState === Pdp2026ScrollState.LeftGalleryScroll ||
          scrollState === Pdp2026ScrollState.RightSecondaryScroll ||
          scrollState === Pdp2026ScrollState.FullPageScroll
          ? LEFT_COLUMN_SCROLL_STICKY_TOP_PX
          : null
        : null,
      isLeftColumnScrolling:
        isActive && scrollState === Pdp2026ScrollState.LeftGalleryScroll,
      isRightColumnFixed:
        isActive &&
        (scrollState === Pdp2026ScrollState.LeftGalleryScroll ||
          scrollState === Pdp2026ScrollState.RightSecondaryScroll),
      isRightColumnScrolling:
        isActive && scrollState === Pdp2026ScrollState.RightPanelScrollToCta,
      isFullPageScroll:
        isActive && scrollState === Pdp2026ScrollState.FullPageScroll,
    }),
    [isActive, scrollState]
  );

  return {
    scrollState,
    ...flags,
  };
};

export default usePdp2026ScrollInteraction;
